import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const RequestQuote = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    businessType: '',
    officeSize: '',
    servicesNeeded: [],
    timeline: '',
    budgetRange: '',
    additionalNotes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const businessTypes = [
    'Professional Services',
    'Healthcare',
    'Legal',
    'Finance',
    'Manufacturing',
    'Retail',
    'Technology',
    'Other',
  ];

  const servicesOptions = [
    'Network Architecture',
    'Wi-Fi Design & Setup',
    'Microsoft 365',
    'Cloud Infrastructure',
    'Cybersecurity',
    'IT Support',
    'New Office Setup',
  ];

  const timelineOptions = [
    'Immediate (Within 2 weeks)',
    '1 Month',
    '2-3 Months',
    'Flexible',
  ];

  const budgetRanges = [
    'Under $1,000',
    '$1,000 - $2,500',
    '$2,500 - $5,000',
    '$5,000+',
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCheckboxChange = (service) => {
    const updatedServices = formData.servicesNeeded.includes(service)
      ? formData.servicesNeeded.filter((s) => s !== service)
      : [...formData.servicesNeeded, service];

    setFormData({
      ...formData,
      servicesNeeded: updatedServices,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Map form data to backend format
      const requestData = {
        companyName: formData.companyName,
        contactName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        position: 'Not Specified',
        businessType: formData.businessType,
        officeSize: formData.officeSize,
        servicesNeeded: formData.servicesNeeded,
        timeline: formData.timeline,
        budgetRange: formData.budgetRange,
        additionalNotes: formData.additionalNotes,
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forms/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Quote request submitted! We\'ll contact you within 24 hours.');
        setFormData({
          fullName: '',
          companyName: '',
          email: '',
          phone: '',
          businessType: '',
          officeSize: '',
          servicesNeeded: [],
          timeline: '',
          budgetRange: '',
          additionalNotes: '',
        });
      } else {
        toast.error(data.detail || 'Failed to submit request. Please try again.');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Toaster position="top-center" richColors />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Request a Quote</h1>
          <p className="text-xl text-gray-600">
            Tell us about your IT needs and we'll provide a customized quote within 24 hours.
          </p>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 md:p-12 border border-gray-200 shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Contact Information */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                      placeholder="Acme Corp"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Business Information */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Business Information</h2>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-2">
                      Business Type or Industry *
                    </label>
                    <select
                      id="businessType"
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                    >
                      <option value="">Select your industry</option>
                      {businessTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="officeSize" className="block text-sm font-medium text-gray-700 mb-2">
                      Office Size or Number of Devices *
                    </label>
                    <input
                      type="text"
                      id="officeSize"
                      name="officeSize"
                      value={formData.officeSize}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                      placeholder="e.g., 20 employees, 30 devices"
                    />
                  </div>
                </div>
              </div>

              {/* Services Needed */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Services Needed *</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {servicesOptions.map((service) => (
                    <label
                      key={service}
                      className="flex items-center space-x-3 p-4 border border-gray-300 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.servicesNeeded.includes(service)}
                        onChange={() => handleCheckboxChange(service)}
                        className="w-5 h-5 text-[#2995FF] border-gray-300 rounded focus:ring-[#2995FF]"
                      />
                      <span className="text-gray-700">{service}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Project Details */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Project Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Timeline *
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                    >
                      <option value="">Select timeline</option>
                      {timelineOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="budgetRange" className="block text-sm font-medium text-gray-700 mb-2">
                      Budget Range *
                    </label>
                    <select
                      id="budgetRange"
                      name="budgetRange"
                      value={formData.budgetRange}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                    >
                      <option value="">Select budget range</option>
                      {budgetRanges.map((range) => (
                        <option key={range} value={range}>
                          {range}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label htmlFor="additionalNotes" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes or Requirements
                </label>
                <textarea
                  id="additionalNotes"
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                  placeholder="Tell us more about your specific needs, challenges, or questions..."
                />
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting || formData.servicesNeeded.length === 0}
                  className="w-full px-8 py-4 bg-[#2995FF] text-white rounded-lg font-semibold hover:bg-[#004AAD] disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-lg"
                >
                  {isSubmitting ? (
                    <span>Submitting...</span>
                  ) : (
                    <>
                      <span>Submit Quote Request</span>
                      <Send size={20} />
                    </>
                  )}
                </button>
                <p className="text-sm text-gray-500 text-center mt-4">
                  We'll review your request and get back to you within 24 hours.
                </p>
              </div>
            </form>
          </div>

          {/* Benefits */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'Fast Response',
                description: 'Get your customized quote within 24 hours',
              },
              {
                title: 'No Obligation',
                description: 'Free consultation with no commitment required',
              },
              {
                title: 'Expert Advice',
                description: 'Benefit from our years of IT experience',
              },
            ].map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="text-[#2995FF] mt-1 flex-shrink-0" size={20} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RequestQuote;
